import { EditorEvents } from "@tiptap/react";
import { useEffect } from "react";
import {
  Editor,
  EditorBoldItalicUnderlineGroup,
  EditorBulletList,
  EditorContent,
  editorGenerateHTML,
  EditorOrderedList,
  EditorToolbar,
  EditorToolbarSeparator,
  useEditor,
} from "./editor";
import { EditorProps } from "@tiptap/pm/view";

export type EditorInputProps = {
  initialHtml: string;
  onHtmlChange: (html: string) => void;
  editorProps?: EditorProps;
  disabled?: boolean;
};

/**
 * EditorInput is a convenience abstraction to cover the common use
 * case of the Editor being used to handle a special text field.
 * It contains common editing options and a title and description
 * to explain the purpose of the field.
 * It expects an initial HTML value and returns updated HTML through
 * the onHtmlChange callback.
 */
export function EditorInput({
  initialHtml,
  onHtmlChange,
  disabled = false,
  editorProps,
}: EditorInputProps) {
  const editor = useEditor(
    editorProps
      ? {
          content: initialHtml,
          onUpdate: (event: EditorEvents["update"]) => {
            if (disabled) return;
            onHtmlChange(editorGenerateHTML(event.editor));
          },
          editable: !disabled,
          editorProps,
        }
      : {
          content: initialHtml,
          onUpdate: (event: EditorEvents["update"]) => {
            if (disabled) return;
            onHtmlChange(editorGenerateHTML(event.editor));
          },
          editable: !disabled,
        },
  );

  useEffect(() => {
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  return (
    <Editor editor={editor}>
      <EditorToolbar>
        <EditorBoldItalicUnderlineGroup />
        <EditorToolbarSeparator />
        <EditorBulletList />
        <EditorOrderedList />
      </EditorToolbar>
      <EditorContent />
    </Editor>
  );
}
